<template>
  <div class="dashboard">
    <!-- Header -->
    <div class="monaco-card" style="margin: -24px -24px 24px -24px; border-radius: 8px 8px 0 0; border-left: none; border-right: none; border-top: none;">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <h1 class="text-3xl font-bold">Dashboard</h1>
        <p class="mt-2 text-gray-600">
          Welcome to your Vue 3 + Vite dashboard
        </p>
      </div>
    </div>

    <!-- Main Content -->
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <!-- Stats Grid -->
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div
          v-for="stat in dashboardStats"
          :key="stat.title"
          class="bg-white p-6 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow duration-200"
        >
          <div class="flex items-center">
            <div class="p-3 rounded-lg" :class="stat.bgColor">
              <div class="w-6 h-6 text-white flex items-center justify-center">
                📊
              </div>
            </div>
            <div class="ml-4">
              <p class="text-sm font-medium text-gray-600">{{ stat.title }}</p>
              <p class="text-2xl font-semibold text-gray-900">{{ stat.value }}</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Charts Section -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        <!-- Performance Chart -->
        <div class="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <h3 class="text-lg font-semibold text-gray-900 mb-4">Performance Metrics</h3>
          <div class="space-y-4">
            <div v-for="metric in performanceMetrics" :key="metric.name">
              <div class="flex justify-between items-center mb-2">
                <span class="text-sm font-medium text-gray-700">{{ metric.name }}</span>
                <span class="text-sm text-gray-500">{{ metric.value }}ms</span>
              </div>
              <div class="w-full bg-gray-200 rounded-full h-2">
                <div
                  class="h-2 rounded-full transition-all duration-500"
                  :class="metric.color"
                  :style="{ width: metric.percentage + '%' }"
                ></div>
              </div>
            </div>
          </div>
        </div>

        <!-- Activity Chart -->
        <div class="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <h3 class="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h3>
          <div class="space-y-4">
            <div
              v-for="activity in recentActivity"
              :key="activity.id"
              class="flex items-start space-x-3 p-3 hover:bg-gray-50 rounded-lg transition-colors duration-200"
            >
              <div class="p-2 rounded-full" :class="activity.bgColor">
                <div class="w-4 h-4 text-white flex items-center justify-center text-xs">
                  {{ activity.icon }}
                </div>
              </div>
              <div class="flex-1">
                <p class="text-sm font-medium text-gray-900">{{ activity.title }}</p>
                <p class="text-xs text-gray-500">{{ activity.time }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Features Grid -->
      <div class="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <h3 class="text-lg font-semibold text-gray-900 mb-6">Vue 3 + Vite Features</h3>
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div
            v-for="feature in features"
            :key="feature.name"
            class="p-4 border border-gray-200 rounded-lg hover:border-blue-300 hover:shadow-sm transition-all duration-200"
          >
            <div class="flex items-center space-x-3 mb-2">
              <div class="p-2 bg-blue-100 rounded-lg">
                <component :is="feature.icon" class="w-4 h-4 text-blue-600" />
              </div>
              <h4 class="font-medium text-gray-900">{{ feature.name }}</h4>
            </div>
            <p class="text-sm text-gray-600">{{ feature.description }}</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'

const dashboardStats = ref([
  {
    title: 'Build Time',
    value: '0.5s',
    bgColor: 'bg-blue-500'
  },
  {
    title: 'Bundle Size',
    value: '45KB',
    bgColor: 'bg-green-500'
  },
  {
    title: 'Performance',
    value: '98/100',
    bgColor: 'bg-purple-500'
  },
  {
    title: 'Components',
    value: '12',
    bgColor: 'bg-orange-500'
  }
])

const performanceMetrics = ref([
  {
    name: 'First Contentful Paint',
    value: 1.2,
    percentage: 85,
    color: 'bg-green-500'
  },
  {
    name: 'Largest Contentful Paint',
    value: 2.1,
    percentage: 90,
    color: 'bg-blue-500'
  },
  {
    name: 'Time to Interactive',
    value: 1.8,
    percentage: 88,
    color: 'bg-purple-500'
  },
  {
    name: 'Cumulative Layout Shift',
    value: 0.1,
    percentage: 95,
    color: 'bg-orange-500'
  }
])

const recentActivity = ref([
  {
    id: 1,
    title: 'Application deployed successfully',
    time: '2 minutes ago',
    icon: '✓',
    bgColor: 'bg-green-100'
  },
  {
    id: 2,
    title: 'New component added to dashboard',
    time: '1 hour ago',
    icon: 'ℹ️',
    bgColor: 'bg-blue-100'
  },
  {
    id: 3,
    title: 'Performance optimization completed',
    time: '3 hours ago',
    icon: '⚡',
    bgColor: 'bg-purple-100'
  },
  {
    id: 4,
    title: 'Build configuration updated',
    time: '5 hours ago',
    icon: '⚙️',
    bgColor: 'bg-orange-100'
  }
])

const features = ref([
  {
    name: 'Composition API',
    description: 'Modern and flexible way to organize component logic',
    icon: '🎯'
  },
  {
    name: 'Reactivity System',
    description: 'Fine-grained reactivity for better performance',
    icon: '⚡'
  },
  {
    name: 'TypeScript Support',
    description: 'Full TypeScript support for better development experience',
    icon: '📝'
  },
  {
    name: 'Hot Module Replacement',
    description: 'Instant updates without page reload during development',
    icon: '🔄'
  },
  {
    name: 'Optimized Builds',
    description: 'Fast and optimized production builds',
    icon: '📦'
  },
  {
    name: 'Plugin Ecosystem',
    description: 'Rich ecosystem of plugins for extended functionality',
    icon: '🔌'
  }
])

onMounted(() => {
  // Simulate real-time updates
  setInterval(() => {
    // Update random stat
    const randomStat = Math.floor(Math.random() * dashboardStats.value.length)
    const randomValue = Math.floor(Math.random() * 100)
    if (randomStat === 0) {
      dashboardStats.value[0].value = `${(randomValue / 100).toFixed(1)}s`
    } else if (randomStat === 1) {
      dashboardStats.value[1].value = `${randomValue}KB`
    } else if (randomStat === 2) {
      dashboardStats.value[2].value = `${randomValue}/100`
    }
  }, 5000)
})
</script>

<style scoped>
.dashboard {
  min-height: calc(100vh - 200px);
}
</style>
